/**
 * The RESULT screen. Full-frame canvas, 1920x1080 design space.
 *
 * Graded against reference/crazy-taxi/stills/ct-t636.png: a red banner with the word in
 * huge white display type and the run count tucked to the right, a white body, stamped
 * letter-tile headers, one enormous black total, a CLASS stamp and a RANKING plate.
 * Our additions are the works-department hazard stripes and the PowerWash before/after
 * payoff — the transformation IS the satisfaction beat.
 *
 * Every animated value is a pure function of (sim - resultAt).
 */

import { C, alpha, displayFamily } from './theme.js';
import { drawNum, drawLabel, measureLabel, covers, measure } from './typeface.js';
import {
  roundRect, hazard, marquee, stampedTiles, segBar, gaugeRing, asphaltThumb, star,
  tileSeams, tileCentre,
} from './shapes.js';
import { ease, clamp01 } from '../core/mathx.js';
import { fmtInt, fmtMoney } from './hud.js';
import TUNING from './tuning.js';

const T = TUNING;
const ph = (sim, at, dur) => clamp01((sim - at) / dur);

export function drawResult(g, m, age) {
  const R = T.result;
  const L = R.layout;
  const t = R.t;
  const fam = displayFamily();
  const W = T.design.width, H = T.design.height;

  g.clearRect(0, 0, W, H);

  // ---------------------------------------------------------------- backdrop
  const bd = ease.outCubic(ph(age, t.backdrop, t.backdropDur));
  g.save();
  g.globalAlpha = bd;
  g.fillStyle = C.cream;
  g.fillRect(0, 0, W, H);
  // faint radial burst so the eye lands centre-left
  const burst = g.createRadialGradient(W * 0.32, H * 0.5, 40, W * 0.32, H * 0.5, W * 0.7);
  burst.addColorStop(0, alpha(C.white, 0.9));
  burst.addColorStop(1, alpha('#C9C4B4', 0.55));
  g.fillStyle = burst;
  g.fillRect(0, 0, W, H);
  // speed lines — steep and faint, so they read as motion rather than as ruled paper
  g.save();
  g.globalAlpha = bd * 0.045;
  g.strokeStyle = C.ink;
  g.lineWidth = 8;
  for (let i = -8; i < 30; i++) {
    const x = i * 92;
    g.beginPath();
    g.moveTo(x, 0);
    g.lineTo(x + 420, H);
    g.stroke();
  }
  g.restore();
  hazard(g, 0, 0, W, R.hazardStripe, 46, 0, C.orange, C.ink);
  hazard(g, 0, H - R.hazardStripe, W, R.hazardStripe, 46, 23, C.orange, C.ink);
  g.restore();

  // ---------------------------------------------------------------- banner
  const bn = R.banner;
  const sh = R.shadow;
  const bp = ease.outBack(ph(age, t.banner, t.bannerDur));
  g.save();
  g.globalAlpha = clamp01(bp * 1.4);
  g.translate((1 - bp) * -900, 0);

  g.save();
  g.translate(0, bn.y + bn.h);
  g.transform(1, 0, Math.tan(bn.skewDeg * Math.PI / 180), 1, 0, 0);
  g.translate(0, -(bn.y + bn.h));
  g.fillStyle = C.ink;
  g.fillRect(bn.x + sh.dx, bn.y + sh.dy, bn.w, bn.h);
  // FLAT orange. The round-6 two-stop ramp plus a white band over the top 30 % of the banner
  // put a hard horizontal seam through the letterforms at mid-cap height; the value step is
  // now a thin bevel along the very top edge, above the ascender line of every glyph.
  g.fillStyle = C.orange;
  g.fillRect(bn.x, bn.y, bn.w, bn.h);
  g.fillStyle = alpha(C.white, 0.20);
  g.fillRect(bn.x, bn.y, bn.w, bn.bevelH);
  g.fillStyle = alpha(C.ink, 0.16);
  g.fillRect(bn.x, bn.y + bn.h - bn.bevelH * 0.6, bn.w, bn.bevelH * 0.6);
  g.restore();

  drawLabel(g, 'RÉSULTAT', bn.x + bn.streetIndent, bn.titleBaselineY, {
    size: bn.titleSize, family: fam, tracking: 6, skewDeg: bn.skewDeg,
    fill: C.white, outline: 0, squeeze: 0.9,
    shadowDx: sh.dx, shadowDy: sh.dy, shadowAlpha: sh.typeAlpha,
  });
  // Rubric item 8 — the named street, plain readable text, at the top of the panel.
  drawLabel(g, m.street || bn.streetFallback, bn.x + bn.streetIndent + 4, bn.streetBaselineY, {
    size: bn.streetSize, family: fam, tracking: 5, skewDeg: bn.skewDeg,
    fill: C.white, outline: 0, squeeze: 0.95,
  });
  drawNum(g, String(m.result.repaired), bn.x + bn.w - 60, bn.countBaselineY, {
    capH: bn.countSize, weight: 0.23, tracking: 0.03, skewDeg: bn.skewDeg, align: 'right',
    fill: C.white, fillLow: null, gloss: null, outline: 0,
  });
  drawLabel(g, 'NIDS-DE-POULE BOUCHÉS', bn.x + bn.w - 60, bn.subBaselineY, {
    size: bn.subSize, family: fam, tracking: 3, skewDeg: bn.skewDeg, align: 'right',
    fill: alpha(C.white, 0.92), outline: 0, squeeze: 0.95,
  });
  g.restore();

  // ---------------------------------------------------------------- body panel
  const bo = R.body;
  g.save();
  g.globalAlpha = bd;
  g.fillStyle = C.ink;
  g.fillRect(bo.x + sh.dx, bo.y + sh.dy, bo.w, bo.h);
  g.fillStyle = C.white;
  g.fillRect(bo.x, bo.y, bo.w, bo.h);
  g.strokeStyle = C.ink;
  g.lineWidth = 6;
  g.strokeRect(bo.x, bo.y, bo.w, bo.h);
  g.restore();

  // ---------------------------------------------------------------- left column: totals
  drawTotal(g, m, age, fam, {
    label: 'ARGENT TOTAL', at: t.cash, roll: t.cashRoll,
    x: L.leftX, tileY: L.cashTileY, numY: L.cashNumY, capH: L.cashCapH,
    value: m.result.cash, money: true,
  });

  drawTotal(g, m, age, fam, {
    label: 'POINTS', at: t.score, roll: t.scoreRoll,
    x: L.leftX, tileY: L.scoreTileY, numY: L.scoreNumY, capH: L.scoreCapH,
    value: m.result.score, money: false,
  });
  dashRule(g, L.leftX, L.scoreNumY + 26, L.colW, bd);

  // ---------------------------------------------------------------- stat chips
  const chips = m.chips;
  for (let i = 0; i < chips.length; i++) {
    const a = ease.outBack(ph(age, t.chips + i * t.chipStagger, t.chipDur));
    if (a <= 0) continue;
    const cx = L.leftX + i * (L.chipW + L.chipGap);
    g.save();
    g.globalAlpha = clamp01(a * 1.6);
    g.translate(cx + L.chipW * 0.5, L.chipY + L.chipH * 0.5);
    g.scale(a, a);
    g.translate(-(cx + L.chipW * 0.5), -(L.chipY + L.chipH * 0.5));

    roundRect(g, cx + sh.dx, L.chipY + sh.dy, L.chipW, L.chipH, 10);
    g.fillStyle = C.ink;
    g.fill();
    // One dark plate, one accent. Round 3 gave each chip its own hue (navy / orange / azure),
    // which alone put three of the panel's five accent hues on screen.
    roundRect(g, cx, L.chipY, L.chipW, L.chipH, 10);
    g.fillStyle = C.navy;
    g.fill();
    g.strokeStyle = C.ink; g.lineWidth = 4; g.stroke();

    // Two stacked rows: label band measured from the top, value baseline from the bottom.
    drawLabel(g, chips[i].label, cx + 18, L.chipY + L.chipLabelY, {
      size: L.chipLabelSize, family: fam, tracking: 3,
      fill: alpha(C.white, 0.92), outline: 0, squeeze: 0.95,
    });
    drawNum(g, chips[i].value, cx + L.chipW - 18, L.chipY + L.chipH - L.chipValY, {
      capH: L.chipValCapH, weight: 0.21, tracking: 0.03, skewDeg: 0, align: 'right',
      fill: C.orange, fillLow: null, gloss: null, outline: 0,
    });
    g.restore();
  }

  // ---------------------------------------------------------------- CLASSE stamp
  const sp = ph(age, t.stamp, t.stampDur);
  if (sp > 0) {
    stampedTiles(g, 'CLASSE', L.leftX, L.classTileY, L.tileW, L.tileH, L.tileGap, fam, C.white, C.navy);

    const shake = clamp01(1 - (age - t.stamp) / t.stampShake);
    const sx = Math.sin(age * 97.0) * R.stampShakePx * shake * shake;
    const sy = Math.cos(age * 83.0) * R.stampShakePx * shake * shake;
    const scale = 1 + (R.stampPopScale - 1) * (1 - ease.outBack(sp));

    g.save();
    // The stamp pops at 2.6x and throws speed streaks 1.55 stamp-widths to its left, which put
    // four translucent orange bars straight THROUGH the body panel's black border and out onto
    // the cream backdrop beyond it — visible at 1:1 in the round-10 frame as three pale streaks
    // in the panel's own drop shadow. The whole stamp group is now clipped to the body panel, so
    // nothing it does can cross the one hard rule that says where the panel ends.
    g.beginPath();
    g.rect(bo.x, bo.y, bo.w, bo.h);
    g.clip();
    g.globalAlpha = clamp01(sp * 3);
    g.translate(L.stampX + L.stampS * 0.5 + sx, L.stampY + L.stampS * 0.5 + sy);
    g.rotate(R.stampRotDeg * Math.PI / 180);
    g.scale(scale, scale);

    // ghost speed streaks behind, like the flame watermark on the CT licence stamp
    g.globalAlpha *= 0.13;
    g.fillStyle = C.orange;
    for (let i = 0; i < 4; i++) {
      const w = L.stampS * (1.5 - i * 0.22);
      g.fillRect(-L.stampS * 1.55, -L.stampS * 0.30 + i * L.stampS * 0.20, w, L.stampS * 0.085);
    }
    g.globalAlpha = clamp01(sp * 3);

    roundRect(g, -L.stampS * 0.5, -L.stampS * 0.5, L.stampS, L.stampS, 14);
    g.fillStyle = alpha(C.white, 0.85);
    g.fill();
    g.strokeStyle = C.orange;
    g.lineWidth = 9;
    g.stroke();
    // `drawNum`'s visual extent is exactly `capH`, ending ON the baseline, so a baseline at
    // capH/2 centres the glyph in the plate by construction — it cannot drift into the frame
    // again the way the hand-picked 0.34 did.
    const rankCap = L.stampS * L.stampGlyphCap;
    if (covers(m.result.rank)) {
      drawNum(g, m.result.rank, 0, rankCap * 0.5, {
        capH: rankCap, weight: 0.22, tracking: 0, skewDeg: 0, align: 'center',
        fill: C.orange, fillLow: null, gloss: null, outline: 0,
      });
    } else {
      drawLabel(g, m.result.rank, 0, rankCap * 0.5, {
        size: rankCap * 1.28, family: fam, tracking: 0, align: 'center',
        fill: C.orange, outline: 0, squeeze: 0.95,
      });
    }
    g.restore();
  }

  // ---------------------------------------------------------------- star rating
  // Rubric criterion 4. Earned = gold face, unearned = white face; both carry the identical
  // ink outline, so the two states differ by FILL and not by opacity. Gold is the one hue the
  // single-accent rule explicitly excludes, so this costs the panel nothing.
  {
    const earned = starsEarned(m.result, L.starCount);
    for (let i = 0; i < L.starCount; i++) {
      const a = ease.outBack(ph(age, t.stars + i * t.starStagger, t.starDur));
      if (a <= 0) continue;
      const cx = L.starX + i * L.starGap;
      g.save();
      g.globalAlpha = clamp01(a * 2);
      g.translate(cx, L.starY);
      g.scale(a, a);
      star(g, 0, 0, L.starR, i < earned ? C.yellow : C.white, C.ink, 7);
      // Earned stars get a face bevel; unearned stay flat white so the read is unambiguous.
      if (i < earned) star(g, 0, -L.starR * 0.10, L.starR * 0.46, C.yellowHi, C.ink, 0);
      g.restore();
    }
  }

  // ---------------------------------------------------------------- before / after payoff
  const cp = ease.outCubic(ph(age, t.cards, t.cardDur));
  if (cp > 0) {
    g.save();
    g.globalAlpha = cp;
    // SAME SEED, both cards. `payoff.seedOffset` is 0 and is expected to stay 0 — it exists so
    // that "the two thumbnails are one piece of road" is a value you can read rather than an
    // invariant you have to remember. See tuning.js → result.payoff.
    payoffCard(g, L.beforeX, L.cardY, R.payoff.w, R.payoff.h, 'hole', m.seed, 'AVANT', fam, m.weather);
    payoffCard(g, L.afterX, L.cardY, R.payoff.w, R.payoff.h, 'patch', m.seed + R.payoff.seedOffset, 'APRÈS', fam, m.weather);

    // arrow between them
    const ax = L.beforeX + R.payoff.w + 14, ay = L.cardY + R.payoff.h * 0.5;
    g.beginPath();
    g.moveTo(ax, ay - 26);
    g.lineTo(ax + 46, ay);
    g.lineTo(ax, ay + 26);
    g.closePath();
    g.fillStyle = C.orange;
    g.fill();
    g.strokeStyle = C.ink; g.lineWidth = 4; g.stroke();
    g.restore();
  }

  // ---------------------------------------------------------------- completion badge
  if (cp > 0) {
    g.save();
    g.globalAlpha = cp;
    const frac = m.result.total > 0 ? clamp01(m.result.repaired / m.result.total) : 0;
    /**
     * THE ARC AND THE NUMERAL ARE THE SAME QUANTITY.
     *
     * Round 10: "the completion arc lies by 2.6 points — the drawn sweep does not match the
     * number printed inside it." Two independent causes, both fixed:
     *
     *   · the round end-cap extended the painted sweep 8.12° (2.26 %) past the arc's end — see
     *     `gaugeRing` in shapes.js, which now pulls the butt-stroke back by the cap's own
     *     angular radius so the painted outer edge lands exactly on the value;
     *   · the numeral was FLOORED to an integer (21/24 → 87) while the arc was drawn at the
     *     unfloored 87.5 %. The arc is now driven by the SAME floored integer the numeral
     *     prints, so the two agree by construction and can never drift again.
     *
     * Flooring still satisfies rubric criterion 2 — 100 % is reachable only at repaired ===
     * total, never by rounding 99.x up.
     */
    const pct = Math.floor(frac * 100);
    const rr = L.ringR;
    g.fillStyle = C.ink;
    g.beginPath();
    g.arc(L.ringX + sh.dx, L.ringY + sh.dy, rr + L.ringRim + 3, 0, Math.PI * 2);
    g.fill();
    g.beginPath();
    g.arc(L.ringX, L.ringY, rr + L.ringRim, 0, Math.PI * 2);
    g.fillStyle = C.navy;
    g.fill();
    g.strokeStyle = C.ink; g.lineWidth = 6; g.stroke();
    // Same circle, same stroke width, 12 o'clock start, clockwise. The unfilled remainder is
    // the SAME channel in the accent's own dark value, walled by the ink outline, so it can
    // only ever read as "this much of the gauge is not filled yet".
    gaugeRing(g, L.ringX, L.ringY, rr, L.ringThickness, pct / 100, L.ringStartDeg,
      C.orange, C.orangeDeep, L.ringInk);
    drawNum(g, String(pct), L.ringX + L.ringPctDx, L.ringY + L.ringPctDy, {
      capH: L.ringPctCapH, weight: 0.2, tracking: 0.02, skewDeg: 0, align: 'center',
      fill: C.white, fillLow: null, gloss: null, outline: 0,
    });
    drawLabel(g, '%', L.ringX + L.ringPctSignDx, L.ringY + L.ringPctSignDy, {
      size: L.ringPctSignSize, family: fam, tracking: 0, fill: C.white, outline: 0, squeeze: 0.95,
    });
    drawLabel(g, 'RUE RÉPARÉE', L.ringX, L.ringY + rr + L.ringRim + L.ringLabelDy, {
      size: L.ringLabelSize, family: fam, tracking: 4, align: 'center',
      fill: C.ink, outline: 0, squeeze: 0.95,
    });

    // segmented completion bar to the right of the badge
    const bx = L.compBarX, by = L.compBarY;
    drawLabel(g, 'NIDS-DE-POULE BOUCHÉS', bx, by - 12, {
      size: L.compBarLabelSize, family: fam, tracking: 3, fill: C.ink, outline: 0, squeeze: 0.95,
    });
    roundRect(g, bx - 5, by - 5, L.compBarW + 10, 30, 6);
    g.fillStyle = alpha(C.ink, 0.15);
    g.fill();
    segBar(g, bx, by, L.compBarW, 20, frac, Math.max(4, m.result.total), 3,
      C.orange, alpha(C.ink, 0.22), false);
    drawNum(g, `${m.result.repaired}/${m.result.total}`, bx, by + L.compCountDy, {
      capH: L.compCountCapH, weight: 0.2, tracking: 0.03, skewDeg: 0,
      fill: C.ink, fillLow: null, gloss: null, outline: 0,
    });

    // The next-objective teaser, promoted out of the footer prompt into the dead white void
    // the critic measured below this bar.
    stampedTiles(g, 'PROCHAIN', bx, L.nextTileY, L.tileW, L.tileH, L.tileGap, fam, C.white, C.navy);
    const nextName = m.nextSite || R.banner.nextFallbacks[0];
    const nw = measureLabel(g, nextName, { size: L.nextNameSize, family: fam, tracking: 4, squeeze: 0.95 });
    const nscale = nw > L.compBarW ? L.compBarW / nw : 1;
    drawLabel(g, nextName, bx, L.nextNameY, {
      size: L.nextNameSize, family: fam, tracking: 4, fill: C.ink, outline: 0,
      squeeze: 0.95, scale: nscale,
    });
    g.restore();
  }

  // ---------------------------------------------------------------- prompt
  const pp = ph(age, t.prompt, t.promptDur);
  if (pp > 0) {
    g.save();
    g.globalAlpha = pp;
    drawLabel(g, 'APPUYEZ SUR ESPACE — ENCORE UN TROU', T.design.width * 0.5, L.promptY, {
      size: T.overlay.promptSize, family: fam, tracking: 5, align: 'center', skewDeg: 6,
      fill: C.ink, outline: 0, squeeze: 0.95,
    });
    g.restore();
  }
}

/**
 * Stars earned = completion milestones met, so the row is a pure function of the same
 * repaired/total the badge shows. No new state, no RNG, deterministic.
 */
function starsEarned(r, count) {
  const frac = r.total > 0 ? clamp01(r.repaired / r.total) : 0;
  let n = 0;
  for (let i = 0; i < count; i++) if (frac >= (i + 1) / count - 1e-6) n++;
  return n;
}

function dashRule(g, x, y, w, a) {
  g.save();
  g.globalAlpha = a * 0.45;
  g.strokeStyle = C.ink;
  g.lineWidth = 3;
  g.beginPath();
  for (let i = 0; i < w; i += 22) {
    g.moveTo(x + i, y);
    g.lineTo(x + i + 12, y);
  }
  g.stroke();
  g.restore();
}

function drawTotal(g, m, age, fam, o) {
  const L = T.result.layout;
  const p = clamp01((age - o.at) / 0.26);
  if (p <= 0) return;
  g.save();
  g.globalAlpha = p;
  stampedTiles(g, o.label, o.x, o.tileY, L.tileW, L.tileH, L.tileGap, fam, C.white, C.navy);
  const roll = ease.outQuint(clamp01((age - o.at) / o.roll));
  const v = o.value * roll;
  if (o.money) {
    // ---- rubric item 5: the hero cash figure gets its OWN high-chroma accent plate.
    // Reference §2.3/§2.4 marquee, in the panel's own accent instead of the arcade's red:
    // ink border → cone-orange frame → cream tile cells → navy digits in fixed cells,
    // right-aligned, with a round lamp at each end. It is the only plate of its kind on the
    // panel, which is what makes the number outrank its own label again.
    g.fillStyle = C.ink;
    g.fillRect(o.x + T.result.shadow.dx, L.cashPlateY + T.result.shadow.dy, L.colW, L.cashPlateH);
    const run = marquee(
      g, o.x, L.cashPlateY, L.colW, L.cashPlateH,
      L.cashPlateTiles, L.cashPlateSeam, L.cashPlateBulbR,
      C.orange, C.cream, C.creamShade, C.orangeShade,
    );

    /**
     * ONE GLYPH, ONE CELL — `mission-complete`'s hero object and the thing round 10 measured as
     * broken: "the comma, the '1', the '5' and the '0' all cross their dividers by 12-17 px".
     *
     * The old code set the money as a single right-aligned run at the typeface's own advance
     * (427.6 px for "$3,184.50" at cap height 84) across cells whose pitch was 65.1 px. Nothing
     * made those two numbers agree, so the drift accumulated left-to-right and the split-flap
     * motif — the entire point of the plate — collapsed into a number printed over some lines.
     *
     * Now every character is centred in its own cell, right-aligned so the last digit lands in
     * the last cell, exactly as `hud.js` does on the driving board. The seams are then re-laid
     * ON TOP (§2.3: on a real letterboard the tile joints are in front of the letters), which is
     * only safe BECAUSE each glyph is inside a cell — a seam can no longer bisect a numeral.
     */
    const digits = fmtMoney(v);
    const glyphs = '$' + digits;
    const numOpts = {
      capH: o.capH, weight: 0.22, tracking: 0, skewDeg: 0, align: 'center',
      // FLAT NAVY. Round 10: "hero numerals carry gradients that contradict the flat-fill
      // grammar the rest of the panel honours." §2.1/§2.4 — plate type is flat, never ramped.
      fill: C.navy, fillLow: null, gloss: null, outline: 0,
    };
    const start = run.tiles - glyphs.length;
    if (start >= 0) {
      for (let i = 0; i < glyphs.length; i++) {
        const isSign = i === 0;
        drawNum(g, glyphs[i], tileCentre(run, start + i), o.numY, isSign
          ? { ...numOpts, capH: o.capH * L.cashSignScale }
          : numOpts);
      }
    } else {
      // Overflow (a total richer than the plate was designed for): fall back to one right-aligned
      // run scaled into the cells it has, so it can never spill past the frame. Reachable only
      // above $9,999,999.99, which no run produces.
      const wRun = measure(glyphs, o.capH, 0.22, 0.03);
      const avail = run.w - 24;
      drawNum(g, glyphs, run.x + run.w - 12, o.numY, {
        ...numOpts, tracking: 0.03, align: 'right',
        scale: wRun > avail ? avail / wRun : 1,
      });
    }
    tileSeams(g, run, C.orange);
  } else {
    drawNum(g, fmtInt(v), o.x + L.colW, o.numY, {
      capH: o.capH, weight: 0.22, tracking: 0.03, skewDeg: 0, align: 'right',
      fill: C.ink, fillLow: null, gloss: null, outline: 0,
    });
  }
  g.restore();
}

function payoffCard(g, x, y, w, h, mode, seed, label, fam, weather) {
  const sh = T.result.shadow;
  g.fillStyle = C.ink;
  g.fillRect(x - 6 + sh.dx, y - 6 + sh.dy, w + 12, h + 56);
  g.fillStyle = C.cream;
  g.fillRect(x - 6, y - 6, w + 12, h + 12);
  asphaltThumb(g, x, y, w, h, mode, seed, weather === 'winter', T.result.payoff.sawMargin);
  g.strokeStyle = C.ink;
  g.lineWidth = 5;
  g.strokeRect(x - 6, y - 6, w + 12, h + 12);

  // AVANT/APRÈS was red-vs-green — the one hue pair a red-green dichromat cannot separate,
  // and a second and third accent hue on a panel allowed one. It is now VALUE: the "before"
  // tag is the dark plate colour, the "after" tag is the accent, so the pair separates in
  // greyscale as well as in hue.
  const after = mode !== 'hole';
  g.fillStyle = after ? C.orange : C.navy;
  g.fillRect(x - 6, y + h + 6, w + 12, 44);
  g.strokeStyle = C.ink; g.lineWidth = 4;
  g.strokeRect(x - 6, y + h + 6, w + 12, 44);
  drawLabel(g, label, x + w * 0.5, y + h + 39, {
    size: 34, family: fam, tracking: 6, align: 'center',
    fill: after ? C.ink : C.white, outline: 0, squeeze: 0.95,
  });
}
